import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import api from '../api/axios';
import Toast from './Toast';
import { useToast } from '../hooks/useToast';
import { validateImageFile, formatFileSize } from '../utils/fileValidation';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function PropertyForm({ property, onSave, onCancel }) {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city: '',
    address: '',
    price: '',
    monthly_price: '',
    yearly_price: '',
    bedrooms: '',
    bathrooms: '',
    size: '',
    images: [],
    status: 'available',
    is_full_booking: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const { toasts, success, error: showError, removeToast } = useToast();

  const API_BASE = import.meta.env.VITE_API_URL
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : 'https://api.sewaapartementheedgebylia.my.id';

  const getImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (img.image_url) return img.image_url;
    if (img.image_path) return `${API_BASE}/storage/${img.image_path}`;
    return null;
  };

  const progressSpring = useSpring({
    width: loading ? '100%' : '0%',
    config: { tension: 200, friction: 25 }
  });



  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // Resize jika terlalu besar
        const maxDimension = 1200;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.7;
        let result = canvas.toDataURL('image/jpeg', quality);

        // Kurangi kualitas sampai ukuran di bawah 1.5MB
        const maxSize = 1.5 * 1024 * 1024;
        while (result.length * 0.75 > maxSize && quality > 0.3) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }

        // Additional validation for compressed result to prevent XSS
        if (result.length > 10 * 1024 * 1024) { // 10MB limit for data URL
          reject(new Error('Data gambar terlalu besar setelah kompresi'));
          return;
        }

        resolve(result);
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      const reader = new FileReader();
      reader.onload = (e) => {
        // Validate data URL before setting as image source
        const result = e.target.result;
        if (result && typeof result === 'string' && result.startsWith('data:image/')) {
          const base64Data = result.split(',')[1];
          if (base64Data && /^[A-Za-z0-9+/]*={0,2}$/.test(base64Data)) {
            img.src = result;
          } else {
            reject(new Error('Invalid image data format'));
          }
        } else {
          reject(new Error('Invalid image data'));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const convertFileToBase64 = async (file) => {
    if (file.size > 1024 * 1024) {
      return await compressImage(file);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    setError('');

    if (!files.length) {
      setSelectedFiles([]);
      setPreviewUrls([]);
      return;
    }

    if (files.length > 10) {
      const errorMsg = '⚠️ Maksimal 10 gambar yang dapat dipilih!';
      setError(errorMsg);
      showError(errorMsg);
      return;
    }

    const validFiles = [];
    const previews = [];

    for (const file of files) {
      // Basic validation
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showError(`⚠️ File ${file.name} format tidak didukung!`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        showError(`⚠️ File ${file.name} terlalu besar! Maksimal 5MB.`);
        continue;
      }

      if (file.size < 10 * 1024) {
        showError(`⚠️ File ${file.name} terlalu kecil! Minimal 10KB.`);
        continue;
      }

      validFiles.push(file);

      try {
        const reader = new FileReader();
        const preview = await new Promise((resolve, reject) => {
          reader.onload = async (e) => {
            const result = e.target.result;
            if (result && typeof result === 'string' && result.startsWith('data:image/')) {
              const base64Data = result.split(',')[1];
              if (!base64Data || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data)) {
                reject(new Error('Invalid image data format'));
                return;
              }
              if (result.length > 10 * 1024 * 1024) {
                reject(new Error('Data gambar terlalu besar'));
                return;
              }
              if (file.size > 1024 * 1024) {
                try {
                  const compressed = await compressImage(file);
                  resolve(compressed);
                } catch (error) {
                  resolve(result);
                }
              } else {
                resolve(result);
              }
            } else {
              reject(new Error('Invalid image format'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        previews.push(preview);
      } catch (error) {
        showError(`❌ Gagal memproses ${file.name}!`);
      }
    }

    setSelectedFiles(validFiles);
    setPreviewUrls(previews);
  };

  // Sanitize input to prevent XSS
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input.replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .trim();
  };

  useEffect(() => {
    if (property) {
      setFormData({
        name: sanitizeInput(property.name) || '',
        description: sanitizeInput(property.description) || '',
        city: sanitizeInput(property.city) || '',
        address: sanitizeInput(property.address) || '',
        price: property.price || '',
        monthly_price: property.monthly_price || '',
        yearly_price: property.yearly_price || '',
        bedrooms: property.bedrooms || '',
        bathrooms: property.bathrooms || '',
        size: property.size || '',
        images: [],
        status: property.status || 'available',
        is_full_booking: property.is_full_booking === true || property.is_full_booking === 1 || property.is_full_booking === "1"
      });
      // Simpan foto-foto yang sudah ada dari DB
      const imgs = property.images || (property.image_url ? [{ image_path: property.image_url }] : []);
      setExistingImages(imgs);
      setDeletedImageIds([]);
      setSelectedFiles([]);
      setPreviewUrls([]);
    }
  }, [property]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate and sanitize input data
      if (!formData.name || !formData.city || !formData.address) {
        throw new Error('Name, city, and address are required');
      }

      if (Number(formData.price) <= 0 || Number(formData.bedrooms) < 0 || Number(formData.bathrooms) < 0 || Number(formData.size) <= 0) {
        throw new Error('Please enter valid positive numbers for price, bedrooms, bathrooms, and size');
      }

      // Use FormData for file upload
      const submitData = new FormData();
      submitData.append('name', sanitizeInput(formData.name));
      submitData.append('description', sanitizeInput(formData.description));
      submitData.append('city', sanitizeInput(formData.city));
      submitData.append('address', sanitizeInput(formData.address));
      submitData.append('price', Number(formData.price));
      if (formData.monthly_price) submitData.append('monthly_price', Number(formData.monthly_price));
      if (formData.yearly_price) submitData.append('yearly_price', Number(formData.yearly_price));
      submitData.append('bedrooms', Number(formData.bedrooms));
      submitData.append('bathrooms', Number(formData.bathrooms));
      submitData.append('size', Number(formData.size));
      submitData.append('status', formData.status);
      submitData.append('is_full_booking', formData.is_full_booking ? 1 : 0);

      // Handle multiple image files upload
      if (selectedFiles.length > 0) {
        console.log('Uploading files:', selectedFiles.length);
        selectedFiles.forEach((file, index) => {
          submitData.append(`images[]`, file);
          console.log(`File ${index}:`, file.name, file.size);
        });
      } else {
        console.log('No files selected');
      }

      // Log FormData contents
      for (let pair of submitData.entries()) {
        console.log(pair[0], pair[1]);
      }

      let response;
      if (property) {
        // Hapus foto yang sudah ditandai untuk dihapus
        for (const imgId of deletedImageIds) {
          try {
            await api.delete(`/properties/${property.id}/images/${imgId}`);
          } catch (err) {
            console.warn('Gagal hapus foto id:', imgId, err);
          }
        }
        // For edit, use POST with _method=PUT for Laravel
        submitData.append('_method', 'PUT');
        response = await api.post(`/properties/${property.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        console.log('Property updated:', response.data);
      } else {
        // For create, use POST method
        response = await api.post('/properties', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        console.log('Property created:', response.data);
      }

      success(property ? 'Property updated successfully!' : 'Property created successfully!');
      // Close form and trigger page reload
      onSave();
    } catch (error) {
      console.error('Submit error:', error);
      let errorMsg = 'Failed to save property';

      if (error.response) {
        errorMsg = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMsg = 'Network error: Cannot connect to server. Using mock data for development.';
      } else {
        errorMsg = error.message;
      }

      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 z-[9999] pt-28 overflow-y-auto"
        onClick={(e) => e.target === e.currentTarget && onCancel()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={`backdrop-blur-xl rounded-2xl shadow-2xl border w-full max-h-[90vh] overflow-y-auto ${isDark
            ? 'bg-gray-800/95 border-gray-700/20'
            : 'bg-white/95 border-white/20'
            } ${
            // Mobile: full width with small margins, Desktop: max-width
            'mx-2 sm:mx-4 sm:max-w-2xl mb-12'
            }`}
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`sticky top-0 backdrop-blur-xl border-b rounded-t-2xl p-4 sm:p-6 ${isDark
              ? 'bg-gray-800/95 border-gray-700'
              : 'bg-white/95 border-gray-200'
              }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", damping: 15, delay: 0.2 }}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{property ? t('edit_property') : t('add_property')}</h2>
                  <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('manage_property_info')}</p>
                </motion.div>
              </div>
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onCancel}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          </motion.div>

          {/* Form Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-4 sm:p-6"
          >
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl border ${isDark
                    ? 'bg-red-900/20 border-red-800 text-red-300'
                    : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                >
                  <div className="flex items-center">
                    <motion.svg
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </motion.svg>
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, staggerChildren: 0.1 }}
            >
              {/* Progress Bar */}
              {loading && (
                <div className="w-full bg-gray-200 rounded-full h-1 mb-4">
                  <animated.div
                    style={progressSpring}
                    className={`h-1 rounded-full ${isDark ? 'bg-gradient-to-r from-gray-500 to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}
                  />
                </div>
              )}

              {/* Basic Info */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.4 }}
              >
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full p-3 border rounded-xl transition-all hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder={t('enter_property_name')}
                    required
                  />
                </motion.div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_city')} *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className={`w-full p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder={t('enter_city')}
                    required
                  />
                </div>
              </motion.div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark
                    ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-white/50 border-gray-300 text-gray-900'
                    }`}
                  rows="3"
                  placeholder={t('enter_description')}
                />
              </div>

              {/* Address */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_address')} *</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={`w-full p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark
                    ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-white/50 border-gray-300 text-gray-900'
                    }`}
                  rows="2"
                  placeholder={t('enter_address')}
                  required
                />
              </div>

              {/* Property Details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Harga Harian (Rp) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Harga Bulanan (Rp)</label>
                  <input
                    type="number"
                    value={formData.monthly_price || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="Opsional"
                  />
                </div>

                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Harga Tahunan (Rp)</label>
                  <input
                    type="number"
                    value={formData.yearly_price || ''}
                    onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="Opsional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">

                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Kamar Tidur *</label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Kamar Mandi *</label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-xs sm:text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Luas (m²) *</label>
                  <input
                    type="number"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    className={`w-full p-2 sm:p-3 border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDark
                      ? 'bg-gray-700/50 border-gray-600 text-gray-100 placeholder-gray-400'
                      : 'bg-white/50 border-gray-300 text-gray-900'
                      }`}
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              {/* Property Image Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`p-4 sm:p-6 rounded-xl border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-100'}`}
              >
                <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_image')} ({t('optional')})</label>

                {/* Foto yang sudah ada (saat edit) */}
                {existingImages.length > 0 && (
                  <div className="mb-4">
                    <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Foto yang sudah ada ({existingImages.filter(img => !deletedImageIds.includes(img.id)).length} foto):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {existingImages.map((img) => {
                        const url = getImageUrl(img);
                        const isDeleted = deletedImageIds.includes(img.id);
                        if (!url) return null;
                        return (
                          <div key={img.id || url} className="relative">
                            <img
                              src={url}
                              alt="Foto unit"
                              className={`w-20 h-16 rounded-lg object-cover border-2 shadow transition-all ${isDeleted
                                  ? 'opacity-30 grayscale border-red-400'
                                  : isDark ? 'border-gray-500' : 'border-blue-300'
                                }`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (isDeleted) {
                                  // Batalkan hapus
                                  setDeletedImageIds(prev => prev.filter(id => id !== img.id));
                                } else {
                                  // Tandai untuk dihapus
                                  setDeletedImageIds(prev => [...prev, img.id]);
                                }
                              }}
                              className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center shadow-md font-bold ${isDeleted ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                                }`}
                              title={isDeleted ? 'Batalkan hapus' : 'Hapus foto ini'}
                            >
                              {isDeleted ? '↩' : '✕'}
                            </button>
                            {img.is_primary && !isDeleted && (
                              <span className="absolute bottom-0.5 left-0.5 bg-yellow-400 text-yellow-900 text-[9px] font-bold px-1 rounded">
                                Utama
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {deletedImageIds.length > 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        ⚠️ {deletedImageIds.length} foto akan dihapus saat disimpan
                      </p>
                    )}
                  </div>
                )}

                {/* Upload foto baru */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {previewUrls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {previewUrls.map((url, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-center"
                        >
                          <img
                            src={url}
                            alt={`Preview ${i + 1}`}
                            className="w-20 h-16 rounded-lg object-cover border-2 border-blue-400 shadow-lg"
                          />
                          <p className={`text-[10px] mt-0.5 font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Baru</p>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex-1 w-full"
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      multiple
                      onChange={handleFileChange}
                      className={`w-full p-2 sm:p-3 border-2 border-dashed rounded-xl transition-colors file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-2 file:px-2 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-medium ${isDark ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500 file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500 text-gray-300' : 'border-blue-300 bg-white/50 hover:border-blue-400 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'}`}
                    />
                    <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Tambah foto baru (Max 10 files) — foto baru akan ditambahkan ke koleksi
                      {selectedFiles.length > 0 && (
                        <span className={`block font-medium mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          Dipilih: {selectedFiles.length} file baru
                        </span>
                      )}
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('property_status')}</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={`w-full p-3 border rounded-xl transition-all hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDark
                    ? 'bg-gray-700/50 border-gray-600 text-gray-100'
                    : 'bg-white/50 border-gray-300 text-gray-900'
                    }`}
                >
                  <option value="available">Available</option>
                  <option value="rented">Rented</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}
              >
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex-1 text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:ring-gray-500' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-200'}`}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      {t('saving')}
                    </>
                  ) : (
                    <>
                      <motion.svg
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </motion.svg>
                      {property ? t('update_property') : t('create_property')}
                    </>
                  )}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={onCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-6 py-3 border rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center ${isDark
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-600'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-200'
                    }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('cancel')}
                </motion.button>
              </motion.div>
            </motion.form>

            {/* Toast Notifications */}
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                isVisible={true}
                onClose={() => removeToast(toast.id)}
                duration={toast.duration}
              />
            ))}
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}